import { useMemo, useState, useCallback } from "react";
import { Avatar } from "../../../../../../../components/Avatar/Avatar";
import ChevronDownIcon from "../../../../../../../assets/svg/chevron-down-icon";
import { getFileTypeIcon } from "../../../../../../../utils/functions/file-icon-extractor";
import AttachmentPreviewModal from "../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import InboxEmailBodyIframe from "./InboxEmailBodyIframe";
import type { AttachmentPreviewItem } from "../../../../../../Communications/utils/types";
import type { InboxMail } from "../../../utils/types";
import { getDateTimeByFormat } from "../../../../../../../redux/slices/dateTimeFormatSlice";

interface EmailMessageBlockProps {
  mail: InboxMail;
}

const COLLAPSED_MAX_HEIGHT = 200;

const EmailMessageBlock: React.FC<EmailMessageBlockProps> = ({ mail }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isToDetailsOpen, setIsToDetailsOpen] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);

  const senderName =
    mail.direction === "OUTBOUND" && mail.created_by?.name
      ? mail.created_by.name
      : mail.from_email;
  const displayDate = getDateTimeByFormat(new Date(mail.created_at))?.time;

  const handlePreview = (index: number) => {
    setInitialIndex(index);
    setIsPreviewModalOpen(true);
  };

  const handleOverflowChange = useCallback((overflow: boolean) => {
    setIsOverflowing(overflow);
  }, []);

  const previewAttachments: AttachmentPreviewItem[] = useMemo(() => {
    if (!mail.attachments || mail.attachments.length === 0) return [];
    return mail.attachments.map((a) => ({
      s3_key: a.s3_key ?? "",
      s3_url: a.s3_url ?? "",
      file_name: a.file_name ?? "",
      content_type: a.content_type,
      timestamp: mail.created_at,
    }));
  }, [mail.attachments, mail.created_at]);

  return (
    <div className="px-[12px] py-[12px] flex flex-col gap-[12px]">
      {/* Header */}
      <div className="flex items-start gap-[8px]">
        <Avatar
          size={26}
          text={senderName.charAt(0).toUpperCase()}
          className="shrink-0 mt-[2px]"
        />
        <div className="flex-1 min-w-0 flex flex-col gap-[6px]">
          <div className="flex items-center gap-[8px]">
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              {senderName}
            </span>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            <span className="text-sm text-analytics-gray dark:text-analytics-gray-dk">
              {displayDate}
            </span>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full" />
            <button
              onClick={() => setIsToDetailsOpen(!isToDetailsOpen)}
              className="flex items-center justify-center"
            >
              <ChevronDownIcon
                className={`w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
                  isToDetailsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
          {isToDetailsOpen && (
            <p className="text-sm text-analytics-gray dark:text-analytics-gray-dk">
              To: {mail.to.join(", ")}
            </p>
          )}
          {isToDetailsOpen && mail.cc.length > 0 && (
            <p className="text-sm text-analytics-gray dark:text-analytics-gray-dk">
              Cc: {mail.cc.join(", ")}
            </p>
          )}
          {isToDetailsOpen && mail.bcc.length > 0 && (
            <p className="text-sm text-analytics-gray dark:text-analytics-gray-dk">
              Bcc: {mail.bcc.join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      {isToDetailsOpen && (
        <div className="pl-[34px] overflow-y-hidden">
          {mail?.content?.trim() && (
            <InboxEmailBodyIframe
              html={mail.content}
              maxCollapsedHeight={COLLAPSED_MAX_HEIGHT}
              isExpanded={isExpanded}
              onOverflowChange={handleOverflowChange}
            />
          )}
          {isOverflowing && (
            <button
              className="flex items-center justify-center w-full gap-[4px] text-sm text-verba-blue dark:text-verba-blue-dk font-medium mt-[8px]"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ChevronDownIcon
                className={`w-[8px] h-[8px] transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
              {isExpanded ? "See Less" : "See More"}
            </button>
          )}
          {/* Attachments */}
          {mail.attachments && mail.attachments.length > 0 && (
            <div className="mt-[12px] flex flex-col gap-[2px]">
              <span className="text-xxs uppercase font-medium text-gray-placeholder dark:text-gray-placeholder-dk tracking-wider">
                Attachments
              </span>
              <div className="flex gap-[8px] mt-[4px]">
                {mail.attachments.map((attachment, idx) => (
                  <div
                    key={idx}
                    onClick={() => handlePreview(idx)}
                    className="w-[60px] h-[60px] rounded-[8px] bg-hover-effect dark:bg-hover-effect-dk border border-border-gray dark:border-border-gray-dk flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={getFileTypeIcon(attachment.file_name ?? "")}
                      alt={attachment.file_name ?? ""}
                      className="w-[24px] h-[24px] object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AttachmentPreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        attachments={previewAttachments}
        initialIndex={initialIndex}
      />
    </div>
  );
};

export default EmailMessageBlock;
